import { useEffect, Component, type ErrorInfo } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import NotFound from "@/pages/not-found";
import ClientLogin from "@/pages/client/login";
import ClientOrder from "@/pages/client/order";
import ClientHistory from "@/pages/client/history";
import AdminDashboard from "@/pages/admin/dashboard";
import AdminProducts from "@/pages/admin/products";
import AdminClients from "@/pages/admin/clients";
import AdminSettings from "@/pages/admin/settings";
import AdminUsers from "@/pages/admin/users";
import AdminLogin from "@/pages/admin/login";
import DeliveryNotePage from "@/pages/admin/delivery-note";
import AdminOrdersPage from "@/pages/admin/orders";
import AdminReturns from "@/pages/admin/returns";
import AdminRawMaterials from "@/pages/admin/raw-materials";
import AdminBaseRecipes from "@/pages/admin/base-recipes";
import AdminArticleRecipes from "@/pages/admin/article-recipes";
import AdminInvoicing from "@/pages/admin/invoicing";
import AdminActivityLog from "@/pages/admin/activity-log";
import AdminConnections from "@/pages/admin/connections";
import AdminApiDocs from "@/pages/admin/api-docs";
import AdminSupport from "@/pages/admin/support";
import AdminNotifications from "@/pages/admin/notifications";
import AdminPriceLists from "@/pages/admin/price-lists";
import AdminStatistics from "@/pages/admin/statistics";
import AdminCommunication from "@/pages/admin/communication";
import AdminRoles from "@/pages/admin/roles";
import AdminDeliveryAdjustments from "@/pages/admin/delivery-adjustments";
import PublicDeliveryNotePage from "@/pages/public/delivery-note";
import ClientSupport from "@/pages/client/support";
import ClientNotifications from "@/pages/client/notifications";
import TragarePage from "@/pages/production/tragare";
import BagarePage from "@/pages/production/bagare";
import PakarePage from "@/pages/production/pakare";
import ProductionDeliveryNotePage from "@/pages/production/delivery-note";
import ClientOnboarding from "@/pages/client/onboarding";
import SellerLogin from "@/pages/seller/login";
import SellerDashboard from "@/pages/seller/dashboard";
import SellerClients from "@/pages/seller/clients";
import SellerNewOrder from "@/pages/seller/new-order";
import SellerSettings from "@/pages/seller/settings";
import SellerOrders from "@/pages/seller/orders";
import AdminSellers from "@/pages/admin/sellers";
import ClientLayout from "@/components/layouts/client-layout";
import AdminLayout, { AdminRouteGuard, AccessDeniedView } from "@/components/layouts/admin-layout";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import logoFallback from "@assets/ratt-order-logo.jpeg";

class AppErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary] Caught error:", error.message, error.stack, info.componentStack);
    try {
      fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message,
          stack: error?.stack,
          componentStack: info?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch {}
  }

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || "";
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: "#fff", color: "#000", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
          <h1 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.75rem" }}>Något gick fel</h1>
          <p style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "0.5rem" }}>
            Sidan kunde inte laddas. Försök att ladda om.
          </p>
          {msg ? (
            <p style={{ fontSize: "0.7rem", color: "#9ca3af", marginBottom: "1.5rem", fontFamily: "monospace", wordBreak: "break-all", maxWidth: "32rem" }}>
              {msg}
            </p>
          ) : (
            <div style={{ marginBottom: "1.5rem" }} />
          )}
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "0.5rem 1.5rem", background: "#000", color: "#fff", border: "none", borderRadius: "0.375rem", cursor: "pointer", fontSize: "0.875rem" }}
          >
            Ladda om sidan
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function LoadingScreen({ logoUrl }: { logoUrl: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background">
      <img
        src={logoUrl}
        alt="Logotyp"
        className="h-20 w-auto object-contain"
        onError={(e) => { (e.currentTarget as HTMLImageElement).src = logoFallback; }}
      />
      <div className="flex gap-1.5">
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:-0.3s]" />
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:-0.15s]" />
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce" />
      </div>
    </div>
  );
}

function ClientRoutes() {
  return (
    <ClientLayout>
      <Switch>
        <Route path="/" component={ClientOrder} />
        <Route path="/history" component={ClientHistory} />
        <Route path="/support" component={ClientSupport} />
        <Route path="/notifications" component={ClientNotifications} />
        <Route component={NotFound} />
      </Switch>
    </ClientLayout>
  );
}

function AdminRoutes() {
  return (
    <AdminLayout>
      <AdminRouteGuard>
        <Switch>
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/dashboard" component={AdminDashboard} />
          <Route path="/admin/products" component={AdminProducts} />
          <Route path="/admin/clients" component={AdminClients} />
          <Route path="/admin/orders" component={AdminOrdersPage} />
          <Route path="/admin/returns" component={AdminReturns} />
          <Route path="/admin/raw-materials" component={AdminRawMaterials} />
          <Route path="/admin/base-recipes" component={AdminBaseRecipes} />
          <Route path="/admin/article-recipes" component={AdminArticleRecipes} />
          <Route path="/admin/invoicing" component={AdminInvoicing} />
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin/settings" component={AdminSettings} />
          <Route path="/admin/connections" component={AdminConnections} />
          <Route path="/admin/activity-log" component={AdminActivityLog} />
          <Route path="/admin/api-docs" component={AdminApiDocs} />
          <Route path="/admin/support" component={AdminSupport} />
          <Route path="/admin/notifications" component={AdminNotifications} />
          <Route path="/admin/price-lists" component={AdminPriceLists} />
          <Route path="/admin/statistics" component={AdminStatistics} />
          <Route path="/admin/communication" component={AdminCommunication} />
          <Route path="/admin/roles" component={AdminRoles} />
          <Route path="/admin/delivery-adjustments" component={AdminDeliveryAdjustments} />
          <Route path="/admin/sellers" component={AdminSellers} />
          <Route path="/admin/delivery-note/:id" component={DeliveryNotePage} />
          <Route component={NotFound} />
        </Switch>
      </AdminRouteGuard>
    </AdminLayout>
  );
}

// Map production paths to required permission keys (only applies when an admin session exists)
const PROD_PERM_MAP: Record<string, string> = {
  "/production/tragare": "prod.tragare",
  "/production/bagare": "prod.bagare",
  "/production/pakare": "prod.pakare",
};

function ProductionRoutes({ adminSession }: { adminSession: AdminSession | null | undefined }) {
  const [location] = useLocation();

  // Synchronous denial check — only activates when an admin session exists
  const isDenied = !!adminSession
    && !adminSession.isSuperAdmin
    && (() => {
      const requiredPerm = PROD_PERM_MAP[location];
      return !!requiredPerm && !adminSession.permissions.includes(requiredPerm);
    })();

  if (isDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <AccessDeniedView />
      </div>
    );
  }

  return (
    <Switch>
      <Route path="/production/tragare" component={TragarePage} />
      <Route path="/production/bagare" component={BagarePage} />
      <Route path="/production/pakare" component={PakarePage} />
      <Route path="/production/delivery-note/:id" component={ProductionDeliveryNotePage} />
      <Route component={NotFound} />
    </Switch>
  );
}

interface AdminSession {
  id: string;
  username: string;
  displayName: string;
  isSuperAdmin: boolean;
  permissions: string[];
}

interface ClientSession {
  id: string;
  name: string;
  email?: string;
}

interface SellerSession {
  id: string;
  name: string;
  canRegisterClients: boolean;
  canCreateOrders: boolean;
  canViewOrders: boolean;
}

function useManifestScope() {
  const [location] = useLocation();
  useEffect(() => {
    const link = document.getElementById("manifest-link");
    if (link) {
      const isAdmin = location.startsWith("/admin");
      link.setAttribute("href", isAdmin ? "/manifest.json?scope=admin" : "/manifest.json");
      const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleMeta) {
        appleMeta.setAttribute("content", isAdmin ? "Rätt Admin" : "Rätt Order");
      }
    }
  }, [location]);
}

function Router() {
  const [location, setLocation] = useLocation();
  useManifestScope();

  const logoUrl = useCompanyLogo();

  const { data: adminSession, isLoading: adminSessionLoading } = useQuery<AdminSession | null>({
    queryKey: ["/api/admin-auth/me"],
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { data: clientSession, isLoading: clientSessionLoading } = useQuery<ClientSession | null>({
    queryKey: ["/api/client-auth/me"],
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const isProductionPath = location.startsWith("/production");
  const isAdminPath = location.startsWith("/admin");
  const isSellerPath = location.startsWith("/seller");
  const isOnboardingPath = location === "/onboarding";
  const isPublicDeliveryNote = location.startsWith("/f/");

  const { data: sellerSession, isLoading: sellerSessionLoading } = useQuery<SellerSession | null>({
    queryKey: ["/api/seller-auth/me"],
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    enabled: isSellerPath,
  });

  if (isPublicDeliveryNote) {
    return (
      <Switch>
        <Route path="/f/:token" component={PublicDeliveryNotePage} />
      </Switch>
    );
  }

  if (isProductionPath) {
    return <ProductionRoutes adminSession={adminSession} />;
  }

  if (isSellerPath) {
    if (sellerSessionLoading) {
      return <LoadingScreen logoUrl={logoUrl} />;
    }
    if (sellerSession) {
      return (
        <Switch>
          <Route path="/seller" component={SellerDashboard} />
          <Route path="/seller/clients" component={SellerClients} />
          <Route path="/seller/new-order" component={SellerNewOrder} />
          <Route path="/seller/settings" component={SellerSettings} />
          <Route path="/seller/orders" component={SellerOrders} />
          <Route path="/seller/login">
            {() => { window.location.replace("/seller"); return null; }}
          </Route>
          <Route component={NotFound} />
        </Switch>
      );
    }
    return (
      <Switch>
        <Route path="/seller/login">
          <SellerLogin onLoginSuccess={() => { window.location.replace("/seller"); }} />
        </Route>
        <Route>
          <SellerLogin onLoginSuccess={() => { window.location.replace("/seller"); }} />
        </Route>
      </Switch>
    );
  }

  if (isOnboardingPath) {
    return <ClientOnboarding />;
  }

  if (isAdminPath) {
    if (adminSessionLoading) {
      return <LoadingScreen logoUrl={logoUrl} />;
    }
    
    if (adminSession) {
      return <AdminRoutes />;
    }
    return (
      <AdminLogin
        onLoginSuccess={() => {
          window.location.replace("/admin/dashboard");
        }}
      />
    );
  }

  if (clientSessionLoading) {
    return <LoadingScreen logoUrl={logoUrl} />;
  }

  if (!clientSession) {
    return (
      <ClientLogin
        onLoginSuccess={() => {
          window.location.replace("/");
        }}
      />
    );
  }

  return <ClientRoutes />;
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
